import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { PreLaunchActions } from '@/features/landing/components/PreLaunchActions'
import { signupsOpen } from '@/lib/env'
import { ctaClicked } from '@/lib/analytics'

/**
 * Build-time constant (Vite inlines import.meta.env). Matches the pattern
 * used in Nav.tsx — when signups are closed, an additional row appears
 * beneath the CTAs below to give visitors a real conversion path.
 */
const SIGNUPS_OPEN = signupsOpen()

/**
 * Pre-footer call-to-action band. Single, focused — get them on the squad
 * onboarding flow. Lives between the MobileFirst section and the Footer to
 * give the page a strong terminal beat instead of dribbling into legal links.
 */
export function CTABand() {
  return (
    <section className="relative isolate overflow-hidden border-b border-border/60 px-6 py-24 md:py-32">
      {/* Background wash */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-20"
        style={{ background: 'var(--gradient-accent)' }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(circle at center, rgba(245,245,245,0.6) 1px, transparent 1.5px)',
          backgroundSize: '32px 32px',
          maskImage:
            'radial-gradient(ellipse 60% 60% at 50% 50%, #000 40%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 60% 60% at 50% 50%, #000 40%, transparent 80%)',
        }}
      />

      <div className="mx-auto flex max-w-3xl flex-col items-center gap-8 text-center">
        <p className="font-display text-xs tracking-[0.3em] text-fg-subtle uppercase">
          Ready when you are
        </p>
        <h2 className="font-display text-4xl leading-[1.05] font-semibold text-balance md:text-6xl">
          Free up to 10 members. No card. <br />
          <span className="text-fg-muted">Get your squad in tonight.</span>
        </h2>
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <Button asChild variant="primary" size="lg">
            <Link
              to="/signup"
              onClick={() => ctaClicked('get_started', 'cta_band', '/signup')}
            >
              Get started free
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="lg"
            className="font-display text-fg-muted hover:text-fg"
          >
            <Link
              to="/login"
              onClick={() => ctaClicked('sign_in', 'cta_band', '/login')}
            >
              I already have an account
            </Link>
          </Button>
        </div>

        {/* Additive only. The CTAs above keep their copy and keep funnelling to
            the coming-soon page — a deliberate decision documented in
            e2e/signups-gate.spec.ts. This row disappears when signups open. */}
        {!SIGNUPS_OPEN && <PreLaunchActions />}
      </div>
    </section>
  )
}
